import { cloneElement, useMemo } from "react";

import { Box, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { InventoryChangeReason } from "__graphql__/types";
import { AdditionalOutboundReason } from "flows/Inbound/stores/inboundStore/types";
import { reasonMetadata } from "flows/Inventory/flows/StockCorrections/utils/constants"; // TODO: move to a more general location
import { transformChunks } from "shared/components/TextWrapper";
import { IntlMessageId } from "shared/lang";
import { Accordion } from "ui/Accordion/Accordion";
import { BottomSheet } from "ui/BottomSheet/BottomSheet";
import { Button } from "ui/Button/Button";
import { DropDown } from "ui/DropDown/DropDown";
import { DropDownSection } from "ui/DropDown/DropDownList";
import {
  BoxIcon,
  CalendarWarningIcon,
  CircleCrossIcon,
  InformationIcon,
  PhysicalDamageIcon,
  TagIcon,
} from "ui/Icons/Icons";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { TitleM, DetailL, BodyM, BodyS } from "ui/Typography/Typography";

const ALLOWED_REASONS = [
  InventoryChangeReason.outbound_product_damaged,
  InventoryChangeReason.outbound_product_expired,
];

export type OutboundGroup = "damaged" | "expired";

const subReasonIcons: Record<AdditionalOutboundReason, React.ReactElement> = {
  [AdditionalOutboundReason.missing_or_incorrect_label]: <TagIcon />,
  [AdditionalOutboundReason.packaging_issue]: <BoxIcon />,
  [AdditionalOutboundReason.physically_damage]: <PhysicalDamageIcon />,
  [AdditionalOutboundReason.perished_mold]: <CircleCrossIcon />,
  [AdditionalOutboundReason.wrong_bbd]: <CalendarWarningIcon />,
};

const damagedSubReasons = [
  AdditionalOutboundReason.missing_or_incorrect_label,
  AdditionalOutboundReason.packaging_issue,
  AdditionalOutboundReason.physically_damage,
];

const expiredSubReasons = [
  AdditionalOutboundReason.perished_mold,
  AdditionalOutboundReason.wrong_bbd,
];

export function getOutboundGroup(subReason: AdditionalOutboundReason | null): OutboundGroup | null {
  if (subReason && damagedSubReasons.includes(subReason)) return "damaged";
  if (subReason && expiredSubReasons.includes(subReason)) return "expired";
  return null;
}

function getAdjusterLabelId(group: OutboundGroup | null): IntlMessageId | null {
  if (group === "damaged") return "pages.inbound.inbound-outbound.damaged-adjuster-label";
  if (group === "expired") return "pages.inbound.inbound-outbound.expired-adjuster-label";
  return null;
}

type OutboundReasonProps = {
  outboundCount: number;
  setOutboundCount: (count: number) => void;
  isIncreaseBtnDisabled: boolean;
  selectedSubReason: AdditionalOutboundReason | null;
  setSelectedSubReason: (subReason: AdditionalOutboundReason | null) => void;
};

function OutboundReason({
  outboundCount,
  setOutboundCount,
  isIncreaseBtnDisabled,
  selectedSubReason,
  setSelectedSubReason,
}: OutboundReasonProps) {
  const intl = useIntl();

  const {
    isOpen: isBottomSheetOpen,
    onOpen: openBottomSheet,
    onClose: closeBottomSheet,
  } = useDisclosure();

  const reasonExplanations = useMemo(() => {
    return ALLOWED_REASONS.map((reason) => {
      const reasonMeta = reasonMetadata[reason];
      return {
        key: reason,
        icon: reasonMeta.icon,
        explanationTitle: intl.formatMessage({ id: reasonMeta.explanationTitleId }),
        explanationDescription: intl.formatMessage(
          { id: reasonMeta.explanationDescriptionId },
          { ...transformChunks({ b: { as: "strong" } }), br: <br /> },
        ),
      };
    });
  }, [intl]);

  const subReasonSections: DropDownSection[] = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: "pages.inbound.inbound-outbound.outbound-reason.sub-reason.damaged-group",
        }),
        options: damagedSubReasons.map((subReason) => ({
          key: subReason,
          label: intl.formatMessage({
            id: `pages.inbound.inbound-outbound.outbound-reason.sub-reason.${subReason}`,
          }),
          icon: subReasonIcons[subReason],
        })),
      },
      {
        label: intl.formatMessage({
          id: "pages.inbound.inbound-outbound.outbound-reason.sub-reason.expired-group",
        }),
        options: expiredSubReasons.map((subReason) => ({
          key: subReason,
          label: intl.formatMessage({
            id: `pages.inbound.inbound-outbound.outbound-reason.sub-reason.${subReason}`,
          }),
          icon: subReasonIcons[subReason],
        })),
      },
    ],
    [intl],
  );

  const handleSubReasonChange = (value: string | null) => {
    setSelectedSubReason(value as AdditionalOutboundReason | null);
    setOutboundCount(0);
  };

  const adjusterLabelId = getAdjusterLabelId(getOutboundGroup(selectedSubReason));

  return (
    <>
      <Box bg="white" borderRadius="8px" p="s200">
        <TitleM color="grey.800" mb="s25">
          {intl.formatMessage({ id: "pages.inbound.inbound-outbound.outbound-reason.title" })}
        </TitleM>
        <Button
          variant="ghost"
          size="xs"
          p="0"
          m="0"
          mb="s200"
          onClick={openBottomSheet}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap="s50"
          _hover={{ bg: "transparent" }}
          _active={{ bg: "transparent" }}
        >
          <DetailL color="blue.500">
            {intl.formatMessage({
              id: "inventory-change-reason.reasons_explanations_title",
            })}
          </DetailL>
          <InformationIcon color="blue.500" boxSize="15px" />
        </Button>
        <DropDown
          sections={subReasonSections}
          value={selectedSubReason}
          onInput={handleSubReasonChange}
          filterMode={false}
          border="1px solid"
          borderColor="grey.300"
          borderRadius="8px"
          bgColor="white"
          titleColor="grey.800"
          w="100%"
          h="48px"
          mb="s200"
          testId="outbound-sub-reason-dropdown"
          listStyles={{ w: "320px", maxH: "320px" }}
          listItemStyles={{ w: "320px", minHeight: "48px", color: "grey.800" }}
        >
          <BodyM color="grey.600">
            {intl.formatMessage({
              id: "pages.inbound.inbound-outbound.outbound-reason.sub-reason.placeholder",
            })}
          </BodyM>
        </DropDown>
        {selectedSubReason !== null && (
          <IncreaseDecrease
            changeHandler={setOutboundCount}
            label={
              adjusterLabelId ? (
                <FormattedMessage
                  id={adjusterLabelId}
                  values={transformChunks({ b: { as: "strong" } })}
                />
              ) : undefined
            }
            count={outboundCount}
            isIncreaseBtnDisabled={isIncreaseBtnDisabled}
          />
        )}
      </Box>
      <BottomSheet isOpen={isBottomSheetOpen} onClose={closeBottomSheet}>
        <Accordion allowMultiple defaultIndex={[0, 1]}>
          {reasonExplanations.map((reason) => (
            <Accordion.Item key={reason.key}>
              <Accordion.ItemTrigger>
                {reason.icon && cloneElement(reason.icon, { boxSize: "24px" })}
                <BodyS mx="s100">{reason.explanationTitle}</BodyS>
                <Accordion.ItemIndicator />
              </Accordion.ItemTrigger>
              <Accordion.ItemContent>
                <DetailL>{reason.explanationDescription}</DetailL>
              </Accordion.ItemContent>
            </Accordion.Item>
          ))}
        </Accordion>
      </BottomSheet>
    </>
  );
}

export default OutboundReason;
